/**
 * PHSIMS Version Checker
 * 
 * Detects when a new version is deployed and triggers automatic soft reload.
 * Prevents users from running stale code after deployments.
 * 
 * Features:
 * - Polls backend /api/version endpoint periodically
 * - Compares with current build hash
 * - Shows "Updating..." overlay and auto-reloads
 * - Does NOT force logout - session preserved across reload
 */

// Current build info injected at build time
const currentVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
const currentBuildHash = import.meta.env.VITE_BUILD_HASH || 'dev';

// Check interval (2 minutes in production, disabled in dev if no hash)
const CHECK_INTERVAL_MS = 2 * 60 * 1000;

// Delay before auto-reload (gives user time to see the message)
const AUTO_RELOAD_DELAY_MS = 3000;

let checkIntervalId: ReturnType<typeof setInterval> | null = null;
let updateAvailable = false;
let autoReloadEnabled = true;

/**
 * Fetch the current version from the server
 */
const fetchServerVersion = async (): Promise<{ version: string; build_hash: string } | null> => {
  try {
    const response = await fetch('/api/version', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      // Don't include auth - this should be a public endpoint
    });
    
    if (!response.ok) {
      // If no version endpoint, try manifest.json in static files
      const manifestResponse = await fetch('/manifest.json');
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        return { version: manifest.version || '1.0.0', build_hash: manifest.build_hash || '' };
      }
      return null;
    }
    
    return await response.json();
  } catch (error) {
    // Network error - server might be restarting
    console.debug('[VersionChecker] Could not check version:', error);
    return null;
  }
};

/**
 * Check if a new version is available and auto-reload
 */
export const checkForUpdates = async (): Promise<boolean> => {
  const serverVersion = await fetchServerVersion();
  
  if (!serverVersion) {
    return false;
  }
  
  // Compare build hashes (more reliable than version numbers)
  if (serverVersion.build_hash && currentBuildHash !== 'dev') {
    if (serverVersion.build_hash !== currentBuildHash) {
      console.log(`[VersionChecker] New version available: ${serverVersion.build_hash} (current: ${currentBuildHash})`);
      updateAvailable = true;
      
      // Dispatch event for UI to show update overlay
      window.dispatchEvent(new CustomEvent('phsims:update-available', {
        detail: {
          currentVersion,
          currentBuildHash,
          newVersion: serverVersion.version,
          newBuildHash: serverVersion.build_hash,
          autoReload: autoReloadEnabled,
        }
      }));
      
      // Auto-reload after short delay (gives user time to see the message)
      if (autoReloadEnabled) {
        console.log(`[VersionChecker] Auto-reloading in ${AUTO_RELOAD_DELAY_MS}ms...`);
        setTimeout(() => {
          softReload();
        }, AUTO_RELOAD_DELAY_MS);
      }
      
      return true;
    }
  }
  
  return false;
};

/**
 * Start periodic version checking
 */
export const startVersionChecker = (): void => {
  // Don't start if already running
  if (checkIntervalId) return;
  
  // Don't check in development (no meaningful hash)
  if (currentBuildHash === 'dev' || currentBuildHash === '') {
    console.log('[VersionChecker] Skipping in development mode');
    return;
  }
  
  console.log(`[VersionChecker] Started (current: ${currentBuildHash})`);
  
  // Check immediately once
  setTimeout(() => checkForUpdates(), 10000); // Wait 10s after page load
  
  // Then check periodically
  checkIntervalId = setInterval(checkForUpdates, CHECK_INTERVAL_MS);
};

/**
 * Stop version checking
 */
export const stopVersionChecker = (): void => {
  if (checkIntervalId) {
    clearInterval(checkIntervalId);
    checkIntervalId = null;
  }
};

/**
 * Check if an update is currently available
 */
export const isUpdateAvailable = (): boolean => updateAvailable;

/**
 * Soft reload the page (preserves scroll position where possible)
 */
export const softReload = (): void => {
  // Store current scroll position
  const scrollPos = window.scrollY;
  sessionStorage.setItem('phsims_scroll_pos', String(scrollPos));
  
  // Reload the page
  window.location.reload();
};

/**
 * Restore scroll position after reload
 */
export const restoreScrollPosition = (): void => {
  const scrollPos = sessionStorage.getItem('phsims_scroll_pos');
  if (scrollPos) {
    sessionStorage.removeItem('phsims_scroll_pos');
    // Wait for content to render
    setTimeout(() => {
      window.scrollTo(0, parseInt(scrollPos, 10));
    }, 100);
  }
};

// Export current build info
export const getBuildInfo = () => ({
  version: currentVersion,
  buildHash: currentBuildHash,
  buildDate: import.meta.env.VITE_BUILD_DATE || 'unknown',
});
